import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';

import { BottomNavigationBar } from '../../components/BottomNavigationBar';
import { GenerationDashboard } from '../../components/Generation';
import { StageNavigationBar } from '../../components/StageNavigationBar/StageNavigationBar';
import { STAGE_PAGE_WIDTH_CLASS } from '../../constants/layout';
import {
  BACK_BUTTON_LABEL,
  CONTINUE_BUTTON_LABEL,
} from '../../constants/navigation';
import { useAgentJobsManager } from '../../contexts/AgentJobsContext';
import { useAgentJobs } from '../../hooks/useAgentJobs';
import { adrService } from '../../services/adrService';
import { onAdrUpdated } from '../../services/adrUpdateEvents';
import { AgentJobType, sectionTarget } from '../../services/agentJobTargets';
import { contextManager } from '../../services/contextManager';
import { generationService } from '../../services/generationService';
import {
  clearGenerationSession,
  loadGenerationSession,
  saveGenerationSession,
} from '../../services/generationSessionStore';
import { projectService } from '../../services/projectService';
import { projectStateService } from '../../services/projectStateService';
import { isTerminalStatus } from '../../types/agentJob';
import {
  GenerationState,
  GenerationConfig,
  ProjectContext,
  GenerationStatus,
  GenerationMode,
  ArchitectureSection,
  TOTAL_ARCHITECTURE_SECTIONS,
  SectionStatus,
  DetailLevel,
  GenerationTone,
  AudienceLevel,
  OutputFormat,
  DiagramFormat,
  GenerationLogEntry,
  GeneratedSection,
  GenerationResponse,
  UploadedDocument,
  DocumentType,
  DocumentStatus,
} from '../../types/generation';
import { ProjectItem, ProjectStatus, ProjectContextResponse, ProjectContextUploadedDocumentResponse, ProjectType } from '../../types/project';
import { logger } from '../../utils/logger';
import './Generation.css';

/** Interval (ms) between groundedness polling attempts. */
const GROUNDEDNESS_POLL_INTERVAL_MS = 5000;
/** Maximum number of groundedness polling attempts. */
const GROUNDEDNESS_MAX_POLL_ATTEMPTS = 24;
/** Groundedness score value indicating a pending background calculation. */
const GROUNDEDNESS_PENDING_SCORE = -1;

/** Extract a human-readable message from an unknown thrown value. */
const toErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'Unknown error';

/** Already-generated sections count toward progress only on a partial resume. */
const computeAlreadyDoneCount = (
  regenerateAll: boolean,
  completedCount: number,
): number => (regenerateAll ? 0 : completedCount);

/** On resume, skip a section that is already complete (unless regenerating all). */
const shouldSkipSection = (
  regenerateAll: boolean,
  completedSet: ReadonlySet<ArchitectureSection>,
  section: ArchitectureSection,
): boolean => !regenerateAll && completedSet.has(section);

/** Verb forms used in generation log/error messages, keyed by grammatical form. */
const getGenerationVerbs = (
  isRegeneration: boolean,
): { infinitive: string; present: string; past: string; lower: string } =>
  isRegeneration
    ? { infinitive: 'Regenerate', present: 'Regenerating', past: 'Regenerated', lower: 'regenerate' }
    : { infinitive: 'Generate', present: 'Generating', past: 'Generated', lower: 'generate' };

/** Groundedness needs polling when the score is not yet a real number or still pending. */
const shouldPollGroundedness = (rawScore: number): boolean =>
  Number.isNaN(rawScore) || rawScore === GROUNDEDNESS_PENDING_SCORE;

interface GenerationParams extends Record<string, string | undefined> {
  projectId: string;
  projectTitle?: string;
}

/**
 * Generation page component for project-specific generation tasks
 * @return JSX element for the generation page
 */
export const Generation: React.FC = () => {
  const { projectId } = useParams<GenerationParams>();
  const location = useLocation();
  const navigate = useNavigate();

  // Numeric project id (-1 when the route param is missing) for the tracker.
  const projectIdNum = projectId ? Number.parseInt(projectId, 10) : -1;

  // Navigation-resilient agent-job tracker (mounted at the app root). Section
  // generation is driven through this tracker so an in-flight section survives
  // a full page reload and the sequence can auto-resume without a re-click.
  const jobsManager = useAgentJobsManager();
  const trackedJobs = useAgentJobs(projectIdNum);

  // Guards the one-shot auto-resume so it fires at most once per page load.
  const autoResumeAttemptedRef = useRef(false);

  const [project, setProject] = useState<ProjectItem | null>(null);
  const [projectTitle, setProjectTitle] = useState<string>('Loading...');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [_isEditing, setIsEditing] = useState<boolean>(false);
  const [editedTitle, setEditedTitle] = useState<string>('');
  const [logs, setLogs] = useState<GenerationLogEntry[]>([]);
  const [isGenerationCancelled, setIsGenerationCancelled] = useState(false);
  const [isGenerationPaused, setIsGenerationPaused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // State for tracking generation context for feedback
  const [generationContext, setGenerationContext] = useState<{
    [sectionId: string]: {
      inputs: {
        section: ArchitectureSection;
        userRequest: string;
        projectContext: any;
        timestamp: string;
        isRegeneration: boolean;
      };
      outputs?: {
        content: string;
        groundednessScore: number;
        references: any[];
        processingTime: number;
        success: boolean;
        timestamp: string;
        metadata?: any;
      };
    };
  }>({});

  // Generation state
  const [generationState, setGenerationState] = useState<GenerationState>({
    status: GenerationStatus.IDLE,
    config: {
      mode: GenerationMode.COMPLETE,
      selectedSections: [
        ArchitectureSection.BUSINESS_PURPOSE,
        ArchitectureSection.ADR_SUMMARY,
        ArchitectureSection.CONCEPTUAL_ARCHITECTURE,
        ArchitectureSection.INTEGRATION_ARCHITECTURE,
        ArchitectureSection.SECURITY_ARCHITECTURE,
        ArchitectureSection.TECHNOLOGY_ARCHITECTURE,
        ArchitectureSection.DATA_ARCHITECTURE,
        ArchitectureSection.THREAT_MODELING,
        ArchitectureSection.SOLUTION_SUMMARY,
      ],
      options: {
        includeReferences: true,
        includeDiagrams: true,
        diagramFormats: [DiagramFormat.MERMAID],
        detailLevel: DetailLevel.DETAILED,
        tone: GenerationTone.PROFESSIONAL,
        audienceLevel: AudienceLevel.TECHNICAL,
        modelSettings: {
          primaryModel: 'gpt-4',
          fallbackModel: 'gpt-3.5-turbo',
          temperature: 0.7,
          maxTokens: 4000,
          topP: 0.9,
          frequencyPenalty: 0,
          presencePenalty: 0,
        },
      },
      templates: {
        architectureTemplate: 'shell-standard',
        sectionTemplates: {} as Record<ArchitectureSection, string>,
        customTemplates: {},
      },
      qualitySettings: {
        minimumGroundednessScore: 70,
        requireReferences: true,
        enableRealTimeChecking: true,
        autoRetryOnLowQuality: false,
        maxRetryAttempts: 3,
      },
      outputSettings: {
        format: [OutputFormat.MARKDOWN],
        includeQualityReport: true,
        includeReferences: true,
      },
    },
    isInitialized: false,
    canPause: false,
    canResume: false,
    canCancel: false,
    progress: {
      totalSections: TOTAL_ARCHITECTURE_SECTIONS,
      completedSections: 0,
      currentSectionIndex: 0,
      overallPercentage: 0,
      estimatedTimeRemaining: 0,
      startTime: new Date(),
    },
    sectionProgress: {},
    currentSection: null,
    sections: {}, // Empty sections initially - will be loaded from backend
    metadata: {
      sessionId: `gen-${Date.now()}`,
      startTime: new Date(),
      version: '1.0.0',
      generatedBy: 'current-user',
    },
    overallQuality: {
      groundednessScore: 0,
    },
    references: [],
    logs: [],
    errors: [],
    activeTab: 'overview',
    selectedSection: null,
    showActivityLog: true,
    showQualityPanel: false,
    sessionId: `gen-${Date.now()}`,
    lastSaved: new Date(),
    autoSaveEnabled: true,
  });

  // Individual section generation states
  const [sectionsGenerating, setSectionsGenerating] = useState<Set<ArchitectureSection>>(new Set());
  const [selectedSection, setSelectedSection] = useState<string | null>(null);

  // Sections awaiting background groundedness calculation
  const [pendingGroundedness, setPendingGroundedness] = useState<Set<ArchitectureSection>>(new Set());
  const groundednessTimers = useRef<Map<ArchitectureSection, ReturnType<typeof setInterval>>>(new Map());

  // Confirmation modal state for fresh regenerations (no instructions)
  const [showFreshRegenerationConfirm, setShowFreshRegenerationConfirm] = useState(false);
  const freshRegenerationResolver = useRef<((proceed: boolean) => void) | null>(null);

  // Project context
  const [projectContext, setProjectContext] = useState<ProjectContext>({
    projectId: projectId || '',
    projectTitle: 'Loading...',
    uploadedDocuments: [],
    adrSummary: {
      totalDefined: 8,
      totalGenerated: 8,
      byCategory: {},
      byPriority: {},
      completionPercentage: 100,
      lastUpdated: new Date(),
      pendingDefinitions: [],
      generatedAdrs: [],
    },
    adrCount: 8,
    knowledgeBaseSources: [],
    requirements: [],
    constraints: [],
    metadata: {
      createdAt: new Date(),
      lastModified: new Date(),
      createdBy: 'current-user',
      organizationalUnit: 'Shell',
      businessDomain: 'Enterprise Architecture',
      technologyDomain: 'Cloud Solutions',
      estimatedComplexity: 'medium' as any,
      estimatedDuration: 30,
      confidentialityLevel: 'internal' as any,
    },
  });

  /**
   * Start editing mode
   */
  const _handleEditStart = () => {
    setEditedTitle(projectTitle);
    setIsEditing(true);
    
    // Focus and select text after the state update
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, 0);
  };

  /**
   * Cancel editing and revert to original title
   */
  const handleEditCancel = () => {
    setIsEditing(false);
    setEditedTitle('');
  };

  /**
   * Validate editing and update project title via API
   */
  const handleEditValidate = async () => {
    if (!projectId || !editedTitle.trim()) {
      return;
    }

    try {
      const updatedProject = await projectService.updateProjectTitle(
        Number.parseInt(projectId, 10), 
        editedTitle.trim(),
      );
      setProjectTitle(updatedProject.title);
      setIsEditing(false);
      setEditedTitle('');
      setError(null);
    } catch (err) {
      setError('Failed to update project title');
    }
  };

  /**
   * Handle keyboard events in edit mode
   */
  const _handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleEditValidate();
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  /**
   * Get display name for a section
   */
  const getSectionDisplayName = useCallback((section: ArchitectureSection): string => {
    const displayNames: Record<ArchitectureSection, string> = {
      [ArchitectureSection.BUSINESS_PURPOSE]: 'Business Purpose',
      [ArchitectureSection.SOLUTION_SUMMARY]: 'Solution Summary',
      [ArchitectureSection.ADR_SUMMARY]: 'ADR Summary',
      [ArchitectureSection.CONCEPTUAL_ARCHITECTURE]: 'Conceptual Architecture',
      [ArchitectureSection.DATA_ARCHITECTURE]: 'Data Architecture',
      [ArchitectureSection.INTEGRATION_ARCHITECTURE]: 'Integration Architecture',
      [ArchitectureSection.IMPACTED_COMPONENTS]: 'Impacted Components',
      [ArchitectureSection.SECURITY_ARCHITECTURE]: 'Security Architecture',
      [ArchitectureSection.TECHNOLOGY_ARCHITECTURE]: 'Technology Architecture',
      [ArchitectureSection.DEPLOYMENT_MODEL]: 'Deployment Model',
      [ArchitectureSection.RISK_ASSESSMENT]: 'Risk Assessment',
      [ArchitectureSection.THREAT_MODELING]: 'Threat Modeling',
    };
    return displayNames[section] || section;
  }, []);

  /**
   * Calculate average groundedness score from all sections
   */
  const calculateAverageGroundednessScore = useCallback((
    sections: Partial<Record<ArchitectureSection, GeneratedSection>>,
  ) => {
    const sectionsArray = Object.values(sections).filter(Boolean) as GeneratedSection[];
    // Only consider sections that actually have generated content (avoid placeholders)
    const sectionsWithScores = sectionsArray.filter(section => 
      (section.content && section.content.toString().trim().length > 0) &&
      section.groundednessScore !== undefined && section.groundednessScore !== null && !isNaN(Number(section.groundednessScore)) &&
      Number(section.groundednessScore) >= 0,
    );
    
    if (sectionsWithScores.length === 0) {
      return 0;
    }
    
    const totalScore = sectionsWithScores.reduce((sum, section) => sum + Number(section.groundednessScore), 0);
    return Math.round(totalScore / sectionsWithScores.length);
  }, []);

  /**
   * Calculate most recent ADR timestamp from a list of ADR definitions
   * Considers both creation and update timestamps
   */
  const calculateMostRecentAdrTimestamp = useCallback(
    (adrs: Array<{ createdDate?: string; updatedDate?: string }>): string | null => {
      if (adrs.length === 0) {return null;}

      return adrs.reduce((latest: string | null, adr) => {
        // Get the most recent timestamp for this ADR (creation or update)
        const adrTimestamps = [adr.createdDate, adr.updatedDate].filter(
          (t): t is string => typeof t === 'string',
        );
        if (adrTimestamps.length === 0) {return latest;}

        const mostRecentAdrTimestamp = adrTimestamps.reduce((mostRecent, current) => {
          const mostRecentDate = new Date(mostRecent);
          const currentDate = new Date(current);
          return currentDate > mostRecentDate ? current : mostRecent;
        }, adrTimestamps[0]);

        if (!latest) {return mostRecentAdrTimestamp;}
        const adrDate = new Date(mostRecentAdrTimestamp);
        const latestDate = new Date(latest);
        return adrDate > latestDate ? mostRecentAdrTimestamp : latest;
      }, null as string | null);
    },
    [],
  );

  /**
   * Add ungrounded items metadata to a section for highlighting in the UI
   */
  const addUngroundedItemsToSection = useCallback((
    sectionContent: GeneratedSection,
    ungroundedItems: Record<string, string> | null,
  ): GeneratedSection => {
    if (!ungroundedItems || Object.keys(ungroundedItems).length === 0) {
      return sectionContent;
    }

    // Store ungrounded items in metadata for the rendering component to use
    return {
      ...sectionContent,
      metadata: {
        ...sectionContent.metadata,
        ungroundedItems,
      },
    };
  }, []);

  /**
   * Start polling for a section's groundedness score.
   * Stops when the score is no longer the pending sentinel.
   */
  const startGroundednessPolling = useCallback((
    projectIdNum: number,
    section: ArchitectureSection,
  ) => {
    // Avoid duplicate timers
    if (groundednessTimers.current.has(section)) {return;}

    setPendingGroundedness(prev => {
      const next = new Set(prev);
      next.add(section);
      return next;
    });

    let attempts = 0;
    const timer = setInterval(async () => {
      attempts += 1;
      try {
        const sectionData = await generationService.getSection(
          projectIdNum, section,
        );
        const score = Number(sectionData.section_groundedness);
        if (
          !isNaN(score) &&
          score !== GROUNDEDNESS_PENDING_SCORE
        ) {
          clearInterval(timer);
          groundednessTimers.current.delete(section);
          setPendingGroundedness(prev => {
            const next = new Set(prev);
            next.delete(section);
            return next;
          });
          setGenerationState(prev => {
            const existing = prev.sections[section];
            if (!existing) {return prev;}
            const updatedSections = {
              ...prev.sections,
              [section]: {
                ...existing,
                groundednessScore: score,
              },
            };
            return {
              ...prev,
              sections: updatedSections,
              overallQuality: {
                groundednessScore:
                  calculateAverageGroundednessScore(updatedSections),
              },
            };
          });
        }
      } catch {
        // Silently retry on transient errors
      }
      if (attempts >= GROUNDEDNESS_MAX_POLL_ATTEMPTS) {
        clearInterval(timer);
        groundednessTimers.current.delete(section);
        setPendingGroundedness(prev => {
          const next = new Set(prev);
          next.delete(section);
          return next;
        });
      }
    }, GROUNDEDNESS_POLL_INTERVAL_MS);
    groundednessTimers.current.set(section, timer);
  }, [calculateAverageGroundednessScore]);

  // Cleanup polling timers on unmount
  useEffect(() => {
    const timers = groundednessTimers.current;
    return () => {
      timers.forEach(t => clearInterval(t));
      timers.clear();
    };
  }, []);

  /**
   * Run one section's generation through the navigation-resilient tracker.
   *
   * The tracker owns the polling, so the work continues (and can be adopted by
   * a later page load) even if this component unmounts. When a tracked job for
   * the same section is already in flight, it is awaited instead of starting a
   * duplicate — this is what lets a post-reload resume pick up the section that
   * was already running rather than launching it again.
   *
   * @param targetProjectId - The numeric project id.
   * @param section - The section to generate.
   * @param userRequest - The user prompt passed to the generation agent.
   * @returns The generation response once the job reaches a terminal state.
   */
  const runSectionGenerationJob = useCallback(
    async (
      targetProjectId: number,
      section: ArchitectureSection,
      userRequest: string,
    ): Promise<GenerationResponse> => {
      const target = sectionTarget(section);
      const existing = Object.values(jobsManager.getSnapshot()).find(
        (job) =>
          job.projectId === targetProjectId &&
          job.target === target &&
          !isTerminalStatus(job.status),
      );
      if (existing) {
        return jobsManager.waitFor<GenerationResponse>(existing.jobId);
      }
      return jobsManager.startJobAndWait<GenerationResponse>({
        projectId: targetProjectId,
        jobType: AgentJobType.SECTION_GENERATION,
        target,
        label: `Generating ${getSectionDisplayName(section)}`,
        startFn: generationService.startGenerateSection(
          targetProjectId,
          userRequest,
          section,
        ),
      });
    },
    [jobsManager, getSectionDisplayName],
  );

  /**
   * Generate or regenerate a specific section
   */
  const handleGenerateSection = useCallback(async (
    section: ArchitectureSection, 
    isRegeneration = false,
    customInstructions?: string,
  ) => {
    if (!projectId) {
      setError('Project ID not found');
      return;
    }

    const projectIdNum = Number.parseInt(projectId, 10);
    const startTime = Date.now();
    const verbs = getGenerationVerbs(isRegeneration);
    
    // Dual-mode logic:
    const currentContent = generationState.sections?.[section]?.content || '';

    const hasCustomInstructions = customInstructions && customInstructions.trim().length > 0;

    if (isRegeneration && !hasCustomInstructions) {
      const proceed = await new Promise<boolean>((resolve) => {
        freshRegenerationResolver.current = resolve;
        setShowFreshRegenerationConfirm(true);
      });

      freshRegenerationResolver.current = null;
      setShowFreshRegenerationConfirm(false);

      if (!proceed) {
        setSectionsGenerating(prev => {
          const newSet = new Set(prev);
          newSet.delete(section);
          return newSet;
        });

        setLogs(prev => [...prev, {
          id: `log-${Date.now()}-${section}-cancelled`,
          timestamp: new Date(),
          type: 'action' as any,
          category: 'generation' as any,
          message: `User cancelled regeneration of ${getSectionDisplayName(section)}`,
          metadata: { section, isRegeneration },
        }]);

        return; // Abort
      }
    }

    const willUseEditEndpoint = isRegeneration && hasCustomInstructions;

    const userRequest = customInstructions?.trim() ||
      `${verbs.infinitive} ${getSectionDisplayName(section)} section for the architecture document`;
    
    // Capture generation inputs for feedback
    setGenerationContext(prev => ({
      ...prev,
      [section]: {
        inputs: {
          section,
          userRequest,
          projectContext,
          timestamp: new Date().toISOString(),
          isRegeneration,
        },
        outputs: prev[section]?.outputs, // Keep existing outputs if any
      },
    }));
    
    // Add section to generating set
    setSectionsGenerating(prev => {
      const newSet = new Set(prev);
      newSet.add(section);
      return newSet;
    });

    // Add log entry for section start
    setLogs(prev => [...prev, {
      id: `log-${Date.now()}-${section}`,
      timestamp: new Date(),
      type: 'action' as any,
      category: 'generation' as any,
      message: `${verbs.present} ${getSectionDisplayName(section)} section`,
      metadata: {
        section,
        isRegeneration,
      },
    }]);

    try {
      // Prefer edit endpoint for regenerations so edits preserve/compose with
      // existing content. If custom instructions exist, pass them through;
      let response;
      if (willUseEditEndpoint) {
        const editInstructions = customInstructions.trim();
        response = await generationService.editSection(projectIdNum, section, editInstructions);
      } else {
        response = await runSectionGenerationJob(projectIdNum, section, userRequest);
      }

      if (response.success) {
        // Fetch the generated content
        const sectionData = await generationService.getSection(projectIdNum, section);
        const processingTime = Date.now() - startTime;
        
        if (sectionData.section_content) {
          
          // Capture generation outputs for feedback
          const outputData = {
            content: sectionData.section_content || '',
            groundednessScore: Number(sectionData.section_groundedness) || 0,
            references: [], // References not available in current API
            processingTime,
            success: true,
            timestamp: new Date().toISOString(),
          };
          
          // Update generation context with outputs
          setGenerationContext(prev => ({
            ...prev,
            [section]: {
              ...prev[section],
              outputs: outputData,
            },
          }));
          
          // Update sections with new content
          setGenerationState(prev => {
            const newSection: GeneratedSection = {
              id: `${section}-001`,
              section,
              title: getSectionDisplayName(section),
              content: sectionData.section_content || '',
              status: SectionStatus.COMPLETED,
              groundednessScore: Number(sectionData.section_groundedness) || 0, // Simple groundedness score
              references: [], // Simple references array
              createdAt: new Date(),
              updatedAt: sectionData.section_updated_at ? new Date(sectionData.section_updated_at) : new Date(0),
            };

            // Add ungrounded items if available
            const enrichedSection = addUngroundedItemsToSection(
              newSection,
              sectionData.section_ungrounded_items || null,
            );

            const updatedSections = {
              ...prev.sections,
              [section]: enrichedSection,
            };
            
            const averageGroundedness = calculateAverageGroundednessScore(updatedSections);
            
            return {
              ...prev,
              sections: updatedSections,
              overallQuality: {
                groundednessScore: averageGroundedness,
              },
            };
          });

          // Add log entry for section completion
          setLogs(prev => [...prev, {
            id: `log-${Date.now()}-${section}-complete`,
            timestamp: new Date(),
            type: 'success' as any,
            category: 'generation' as any,
            message: `${verbs.past} ${getSectionDisplayName(section)} section successfully`,
            metadata: {
              section,
              isRegeneration,
            },
          }]);

          // Start polling for the groundedness score if not yet calculated
          const rawScore = Number(sectionData.section_groundedness);
          if (shouldPollGroundedness(rawScore)) {
            startGroundednessPolling(projectIdNum, section);
          }

          await projectStateService.advanceProjectStatus(
            projectIdNum,
            ProjectStatus.GENERATION,
          );
        }
      } else {
        throw new Error(`Failed to ${verbs.lower} ${getSectionDisplayName(section)}`);
      }
    } catch (error) {
      const processingTime = Date.now() - startTime;
      
      // Capture error in generation context
      setGenerationContext(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          outputs: {
            content: '',
            groundednessScore: 0,
            references: [],
            processingTime,
            success: false,
            timestamp: new Date().toISOString(),
            error: toErrorMessage(error),
          },
        },
      }));
      
      setLogs(prev => [...prev, {
        id: `log-${Date.now()}-${section}-error`,
        timestamp: new Date(),
        type: 'error' as any,
        category: 'generation' as any,
        message: `Failed to ${verbs.lower} ${getSectionDisplayName(section)}: ${toErrorMessage(error)}`,
        metadata: {
          section,
          isRegeneration,
          error: toErrorMessage(error),
        },
      }]);

      setError(`Failed to ${verbs.lower} ${getSectionDisplayName(section)}: ${toErrorMessage(error)}`);
    } finally {
      // Remove section from generating set
      setSectionsGenerating(prev => {
        const newSet = new Set(prev);
        newSet.delete(section);
        return newSet;
      });
    }
  }, [
    projectId,
    getSectionDisplayName,
    calculateAverageGroundednessScore,
    generationState.sections,
    projectContext,
    addUngroundedItemsToSection,
    startGroundednessPolling,
    runSectionGenerationJob,
  ]);

  /**
   * Handle updating a generated section
   */
  const handleUpdateSection = useCallback(async (section: ArchitectureSection, content: string) => {
    if (!projectId) {
      setError('Project ID not found');
      return;
    }

    const projectIdNum = Number.parseInt(projectId, 10);

    try {
      // Update the section via API
      await generationService.updateSection(projectIdNum, section, content);

      // Update local state
      setGenerationState(prev => {
        const updatedSections = {
          ...prev.sections,
          [section]: {
            ...prev.sections[section],
            content,
            updatedAt: new Date(),
            groundednessScore: -1,
          },
        };
        
        const averageGroundedness = calculateAverageGroundednessScore(updatedSections);
        
        return {
          ...prev,
          sections: updatedSections,
          overallQuality: {
            groundednessScore: averageGroundedness,
          },
        };
      });

      // Add log entry for section update
      setLogs(prev => [...prev, {
        id: `log-${Date.now()}-update-${section}`,
        timestamp: new Date(),
        type: 'action' as any,
        category: 'generation' as any,
        message: `Updated ${getSectionDisplayName(section)} section`,
        metadata: {
          section,
          action: 'update',
        },
      }]);

    } catch (error) {
      setError(`Failed to update ${getSectionDisplayName(section)}: ${toErrorMessage(error)}`);
      throw error; // Re-throw to allow UI to handle the error
    }
  }, [projectId, getSectionDisplayName, calculateAverageGroundednessScore]);

  /**
   * Generation handlers
   */
  const handleConfigChange = (config: GenerationConfig) => {
    setGenerationState(prev => ({ ...prev, config }));
  };

  /**
   * Load existing sections from backend
   */
  const loadExistingSections = useCallback(async (projectId: number) => {
    try {
      const sectionsToLoad = [
        ArchitectureSection.BUSINESS_PURPOSE,
        ArchitectureSection.ADR_SUMMARY,
        ArchitectureSection.CONCEPTUAL_ARCHITECTURE,
        ArchitectureSection.INTEGRATION_ARCHITECTURE,
        ArchitectureSection.SECURITY_ARCHITECTURE,
        ArchitectureSection.TECHNOLOGY_ARCHITECTURE,
        ArchitectureSection.DATA_ARCHITECTURE,
        ArchitectureSection.THREAT_MODELING,
        ArchitectureSection.SOLUTION_SUMMARY,
      ];

      const loadedSections: Partial<Record<ArchitectureSection, GeneratedSection>> = {};
      let completedCount = 0;

      for (const sectionType of sectionsToLoad) {
        try {
          const response = await generationService.getSection(projectId, sectionType);
          if (response.section_content) {
            
            // Load the section content
            const sectionTimestamp = response.section_updated_at ? new Date(response.section_updated_at) : undefined;
            
            loadedSections[sectionType] = {
              id: `${sectionType}-001`,
              section: sectionType,
              title: getSectionDisplayName(sectionType),
              content: response.section_content || '',
              status: SectionStatus.COMPLETED,
              groundednessScore: Number(response.section_groundedness) || 0,
              references: [], // Empty references array
              createdAt: new Date(),
              updatedAt: sectionTimestamp || new Date(0),
            };
            completedCount++;
          }
        } catch (err) {
          // Section doesn't exist yet, skip
        }
      }

      const averageGroundedness = calculateAverageGroundednessScore(loadedSections);

      setGenerationState(prev => ({
        ...prev,
        sections: loadedSections,
        progress: {
          ...prev.progress,
          completedSections: completedCount,
          overallPercentage: Math.round((completedCount / sectionsToLoad.length) * 100),
        },
        overallQuality: {
          groundednessScore: averageGroundedness,
        },
      }));

    } catch (error) {
      setError('Failed to load existing sections');
    }
  }, [getSectionDisplayName, calculateAverageGroundednessScore]);

  /**
   * Handle section selection and persist to localStorage
   */
  const handleSectionSelect = useCallback(async (sectionId: string | null) => {
    setSelectedSection(sectionId);
    
    // Persist focused section to localStorage
    if (projectId) {
      await projectStateService.updateFocusedSection(
        Number.parseInt(projectId, 10),
        sectionId,
      );
      
      // Update context manager for chat payload
      contextManager.updateContext(
        'section_id',
        sectionId || 'none',
        'Generation',
      );
    }
  }, [projectId]);

  /**
   * Advance project status to GENERATION if not already at that stage or further.
   */
  const advanceProjectToGenerationStatus = useCallback(
    async (projectIdNum: number): Promise<void> => {
      const STATUS_ADVANCE_ORDER = [
        'context_upload', 'context_review',
        'generation', 'in_review', 'completed',
      ];
      try {
        const currentState = await projectStateService.getProjectState(projectIdNum);
        const currentStatus = currentState.project.status ?? 'context_upload';
        if (
          STATUS_ADVANCE_ORDER.indexOf('generation')
          > STATUS_ADVANCE_ORDER.indexOf(currentStatus)
        ) {
          await projectStateService.updateProjectStatus(
            projectIdNum,
            ProjectStatus.GENERATION,
          );
        }
      } catch {
        // Non-critical — generation still proceeds
      }
    },
    [],
  );

  /**
   * Handle the response from generating a single section, updating state and logs.
   * Throws if the section generation was unsuccessful.
   */
  const processSingleSection = useCallback(
    async (
      section: ArchitectureSection,
      sectionIndex: number,
      allSections: ArchitectureSection[],
      projectIdNum: number,
    ): Promise<void> => {
      const response = await runSectionGenerationJob(
        projectIdNum,
        section,
        `Generate ${getSectionDisplayName(section)} section for the architecture document`,
      );

      if (!response.success) {
        throw new Error(`Failed to generate ${getSectionDisplayName(section)}`);
      }

      const sectionData = await generationService.getSection(projectIdNum, section);
      if (!sectionData.section_content) {return;}

      setGenerationState(prev => {
        const updatedSections = {
          ...prev.sections,
          [section]: {
            id: `${section}-001`,
            section,
            title: getSectionDisplayName(section),
            content: sectionData.section_content || '',
            status: SectionStatus.COMPLETED,
            groundednessScore: Number(sectionData.section_groundedness) || 0,
            references: [],
            createdAt: new Date(),
            updatedAt: sectionData.section_updated_at
              ? new Date(sectionData.section_updated_at)
              : new Date(0),
          },
        };
        const averageGroundedness = calculateAverageGroundednessScore(updatedSections);
        return {
          ...prev,
          sections: updatedSections,
          progress: {
            ...prev.progress,
            completedSections: sectionIndex + 1,
            overallPercentage: Math.round(
              ((sectionIndex + 1) / allSections.length) * 100,
            ),
          },
          overallQuality: { groundednessScore: averageGroundedness },
        };
      });

      setLogs(prev => [...prev, {
        id: `log-${Date.now()}-${sectionIndex}-complete`,
        timestamp: new Date(),
        type: 'success' as any,
        category: 'generation' as any,
        message: `Completed ${getSectionDisplayName(section)} section`,
        metadata: { section },
      }]);

      const rawScore = Number(sectionData.section_groundedness);
      if (isNaN(rawScore) || rawScore === GROUNDEDNESS_PENDING_SCORE) {
        startGroundednessPolling(projectIdNum, section);
      }

      setSectionsGenerating(prev => {
        const newSet = new Set(prev);
        newSet.delete(section);
        return newSet;
      });
    },
    [getSectionDisplayName, calculateAverageGroundednessScore, startGroundednessPolling, runSectionGenerationJob],
  );

  /**
   * Start sequential generation
   */
  const handleStartGeneration = useCallback(async () => {
    if (!projectId) {
      setError('Project ID not found');
      return;
    }

    // Clear logs when starting new generation
    setLogs([]);
    setIsGenerationCancelled(false);
    setIsGenerationPaused(false);

    const sections = generationState.config.selectedSections;
    const projectIdNum = Number.parseInt(projectId, 10);

    // Resume support: sections that already have generated content are skipped
    // so a retry continues from where it stopped. When every section is already
    // complete, a fresh "Start Generation" click regenerates the whole document
    // from the first section.
    const completedSet = new Set(
      sections.filter(
        section =>
          generationState.sections[section]?.status === SectionStatus.COMPLETED,
      ),
    );
    const regenerateAll = completedSet.size >= sections.length;
    const alreadyDoneCount = computeAlreadyDoneCount(
      regenerateAll,
      completedSet.size,
    );

    // Persist the run so a full page reload mid-generation can auto-resume the
    // sequence instead of stopping and requiring another click. Cleared once
    // the run finishes (below), errors, or is cancelled.
    saveGenerationSession({
      projectId: projectIdNum,
      sections,
      regenerateAll,
    });

    setGenerationState(prev => ({
      ...prev,
      status: GenerationStatus.RUNNING,
      canPause: true,
      canCancel: true,
      progress: {
        ...prev.progress,
        startTime: new Date(),
        currentSectionIndex: 0,
        completedSections: alreadyDoneCount,
        overallPercentage: Math.round(
          (alreadyDoneCount / sections.length) * 100,
        ),
      },
    }));

    await advanceProjectToGenerationStatus(projectIdNum);

    try {
      for (let i = 0; i < sections.length; i++) {
        // Skip already-generated sections so generation resumes from where it
        // stopped. A full regenerate (all sections complete) ignores this.
        if (shouldSkipSection(regenerateAll, completedSet, sections[i])) {
          continue;
        }

        // Check if generation was cancelled
        if (isGenerationCancelled) {
          break;
        }

        // Check if generation was paused - wait until resumed
        while (isGenerationPaused && !isGenerationCancelled) {
          await new Promise(resolve => setTimeout(resolve, 1000)); // Check every second
        }

        // Check again if cancelled while waiting for pause
        if (isGenerationCancelled) {
          break;
        }

        const section = sections[i];

        // Add section to generating set
        setSectionsGenerating(prev => {
          const newSet = new Set(prev);
          newSet.add(section);
          return newSet;
        });

        // Update progress to show current section
        setGenerationState(prev => ({
          ...prev,
          progress: {
            ...prev.progress,
            currentSectionIndex: i,
            overallPercentage: Math.round((i / sections.length) * 100),
          },
          currentSection: section,
        }));

        // Add log entry for section start
        setLogs(prev => [...prev, {
          id: `log-${Date.now()}-${i}`,
          timestamp: new Date(),
          type: 'action' as any,
          category: 'generation' as any,
          message: `Starting generation of ${getSectionDisplayName(section)}`,
          metadata: {
            section,
            progress: Math.round((i / sections.length) * 100),
          },
        }]);

        await processSingleSection(section, i, sections, projectIdNum);
      }

      // Check final status - if cancelled, don't mark as completed
      if (!isGenerationCancelled) {
        // The run finished; drop the persisted session so it is not resumed.
        clearGenerationSession(projectIdNum);

        // Clear any remaining sections from generating set
        setSectionsGenerating(new Set());

        // Mark as completed
        setGenerationState(prev => ({
          ...prev,
          status: GenerationStatus.COMPLETED,
          canPause: false,
          canResume: false,
          canCancel: false,
          currentSection: null,
          progress: {
            ...prev.progress,
            overallPercentage: 100,
            completedSections: sections.length,
          },
        }));

        setLogs(prev => [...prev, {
          id: `log-${Date.now()}-final`,
          timestamp: new Date(),
          type: 'success' as any,
          category: 'generation' as any,
          message: 'All sections generated successfully',
          metadata: { totalSections: sections.length },
        }]);
      }

    } catch (error) {
      const errorMessage = toErrorMessage(error);

      // A hard failure should not auto-resume on the next load (it would just
      // fail again); drop the persisted session so the user can retry manually.
      clearGenerationSession(projectIdNum);

      // Clear any sections that were being generated
      setSectionsGenerating(new Set());

      setGenerationState(prev => ({
        ...prev,
        status: GenerationStatus.ERROR,
        canPause: false,
        canResume: false,
        canCancel: false,
        currentSection: null,
      }));

      setLogs(prev => [...prev, {
        id: `log-${Date.now()}-error`,
        timestamp: new Date(),
        type: 'error' as any,
        category: 'generation' as any,
        message: `Generation failed: ${errorMessage}`,
        metadata: {
          error: errorMessage,
        },
      }]);

      setError(`Generation failed: ${errorMessage}`);
    }
  }, [
    projectId,
    generationState.config.selectedSections,
    generationState.sections,
    getSectionDisplayName,
    isGenerationCancelled,
    isGenerationPaused,
    advanceProjectToGenerationStatus,
    processSingleSection,
  ]);

  const handlePauseGeneration = () => {
    setIsGenerationPaused(true);
    setGenerationState(prev => ({
      ...prev,
      status: GenerationStatus.PAUSED,
      canPause: false,
      canResume: true,
    }));
    
    setLogs(prev => [...prev, {
      id: `log-${Date.now()}-pause`,
      timestamp: new Date(),
      type: 'action' as any,
      category: 'generation' as any,
      message: 'Generation paused by user',
      metadata: {},
    }]);
  };

  const handleResumeGeneration = () => {
    setIsGenerationPaused(false);
    setGenerationState(prev => ({
      ...prev,
      status: GenerationStatus.RUNNING,
      canPause: true,
      canResume: false,
    }));
    
    setLogs(prev => [...prev, {
      id: `log-${Date.now()}-resume`,
      timestamp: new Date(),
      type: 'action' as any,
      category: 'generation' as any,
      message: 'Generation resumed by user',
      metadata: {},
    }]);
  };

  const handleCancelGeneration = () => {
    setIsGenerationCancelled(true);
    // Clear any sections that were being generated
    setSectionsGenerating(new Set());

    // Drop the persisted run so a later reload does not auto-resume it.
    if (projectId) {
      clearGenerationSession(projectIdNum);
    }

    setGenerationState(prev => ({
      ...prev,
      status: GenerationStatus.CANCELLED,
      canPause: false,
      canResume: false,
      canCancel: false,
      currentSection: null,
    }));
    
    setLogs(prev => [...prev, {
      id: `log-${Date.now()}-cancel`,
      timestamp: new Date(),
      type: 'action' as any,
      category: 'generation' as any,
      message: 'Generation cancelled by user',
      metadata: {},
    }]);
  };

  /**
   * Fetch project data and existing sections on component mount
   */
  useEffect(() => {
    const fetchProject = async () => {
      if (!projectId) {
        setError('Project ID not found');
        setIsLoading(false);
        return;
      }

      try {
        const projectData = await projectService.getProject(Number.parseInt(projectId, 10));
        setProject(projectData);
        setProjectTitle(projectData.title);
        
        // Load existing sections from backend
        await loadExistingSections(Number.parseInt(projectId, 10));
        
        // Load project context for staleness tracking (documents, instructions, ADRs)
        try {
          const contextItems = await projectService.getProjectContext(Number.parseInt(projectId, 10));
          
          // 1. Extract uploaded documents with timestamps
          const uploadedDocs = contextItems
            .filter((item: ProjectContextResponse) => item.type === 'ProjectContextUploadedDocument')
            .map((item: ProjectContextUploadedDocumentResponse) => ({
              id: item.blob_path || '',
              filename: item.name || '',
              type: DocumentType.OTHER,
              size: 0,
              uploadedAt: item.updated_at ? new Date(item.updated_at) : new Date(),
              status: DocumentStatus.PROCESSED,
              metadata: {
                contentType: 'application/pdf',
              },
              updated_at: item.updated_at,
            }));
          
          // 2. Extract instructions timestamp
          const instructionsItem = contextItems.find((item: ProjectContextResponse) => 
            item.type === 'ProjectContextInstructions',
          );
          const instructionsTimestamp = instructionsItem && 'updated_at' in instructionsItem 
            ? instructionsItem.updated_at 
            : undefined;
          
          // Update project context with documents and instructions
          setProjectContext(prev => ({
            ...prev,
            uploadedDocuments: uploadedDocs as UploadedDocument[],
            instructionsUpdatedAt: instructionsTimestamp,
          }));
        } catch (err) {
          logger.error('[Context Loading] Failed to load project context:', err);
        }
        
        // 3. Load ADR timestamps for staleness tracking
        try {
          const projectState = await projectStateService.getProjectState(Number.parseInt(projectId, 10));
          let adrTimestamp = projectState?.adrs?.lastActionTimestamp;
          
          if (!adrTimestamp) {
            const adrs = await adrService.getADRDefinitions(Number.parseInt(projectId, 10));
            adrTimestamp = calculateMostRecentAdrTimestamp(adrs) || undefined;
          }
          
          if (adrTimestamp) {
            setProjectContext(prev => ({
              ...prev,
              adrUpdatedAt: adrTimestamp,
            }));
          }
        } catch (err) {
          logger.error('[Context Loading] Failed to load ADR timestamps:', err);
        }
        
        // Restore focused section from localStorage
        const focusedSection = await projectStateService.getFocusedSection(
          Number.parseInt(projectId, 10),
        );
        if (focusedSection) {
          setSelectedSection(focusedSection);
          // Update context manager for chat payload
          contextManager.updateContext(
            'section_id',
            focusedSection,
            'Generation',
          );
        }
        
        setError(null);
        
        // Update project context
        setProjectContext(prev => ({
          ...prev,
          projectId: projectData.id.toString(),
          projectTitle: projectData.title,
          description: projectData.description || undefined,
        }));
        
        // Check for edit query parameter and start edit mode if present
        const urlParams = new URLSearchParams(location.search);
        if (urlParams.get('edit') === 'title') {
          setEditedTitle(projectData.title);
          setIsEditing(true);
          
          // Focus and select text after the component has rendered
          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.focus();
              inputRef.current.select();
            }
          }, 100);
        }
      } catch (err) {
        setError('Failed to load project');
        setProjectTitle('Unknown Project');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProject();
  }, [projectId, location.search, loadExistingSections, calculateMostRecentAdrTimestamp]);

  // Restore the per-section "generating" spinner for any section whose job is
  // still in flight in the app-root tracker (e.g. after a full page reload),
  // so the UI reflects work that is continuing in the background.
  useEffect(() => {
    const activeSections = trackedJobs
      .filter(
        (job) =>
          job.jobType === AgentJobType.SECTION_GENERATION &&
          !isTerminalStatus(job.status) &&
          typeof job.target === 'string' &&
          job.target.startsWith('section:'),
      )
      .map(
        (job) =>
          (job.target as string).slice('section:'.length) as ArchitectureSection,
      );
    if (activeSections.length === 0) {
      return;
    }
    setSectionsGenerating((prev) => {
      const next = new Set(prev);
      activeSections.forEach((section) => next.add(section));
      return next;
    });
  }, [trackedJobs]);

  // Auto-resume an interrupted run after a full page reload: if a generation
  // session was persisted and sections remain incomplete, continue the
  // sequence automatically (adopting any in-flight section from the tracker)
  // instead of forcing the user to click "Start Generation" again. Runs once.
  useEffect(() => {
    if (isLoading || autoResumeAttemptedRef.current || !projectId) {
      return;
    }
    const session = loadGenerationSession(projectIdNum);
    if (!session) {
      return;
    }
    if (generationState.status === GenerationStatus.RUNNING) {
      return;
    }
    const hasIncompleteSection = session.sections.some(
      (section) =>
        generationState.sections[section]?.status !== SectionStatus.COMPLETED,
    );
    if (!hasIncompleteSection) {
      clearGenerationSession(projectIdNum);
      return;
    }
    autoResumeAttemptedRef.current = true;
    void handleStartGeneration();
  }, [
    isLoading,
    projectId,
    projectIdNum,
    generationState.status,
    generationState.sections,
    handleStartGeneration,
  ]);

  // Listen for section edit events from chat
  useEffect(() => {
    const handleSectionEditStarted = (event: Event) => {
      const customEvent = event as CustomEvent<{
        sectionId: string;
        projectId: number;
        instructions: string;
        source: string;
        autoNavigated?: boolean;
        timestamp: string;
      }>;
      
      const { sectionId, projectId: eventProjectId, instructions, source, autoNavigated } = customEvent.detail || {};
      
      // Verify it's for this project
      if (projectId && eventProjectId && Number.parseInt(projectId, 10) !== Number(eventProjectId)) {
        logger.warn('Section edit event for different project, ignoring');
        return;
      }
      
      
      // Validate section ID
      try {
        const section = sectionId as ArchitectureSection;
        
        // If auto-navigated (user wasn't on Generation page), select the section first
        if (autoNavigated) {
          setSelectedSection(section);
        }
        
        // Add to generating set immediately to show spinner
        setSectionsGenerating(prev => {
          const newSet = new Set(prev);
          newSet.add(section);
          return newSet;
        });
        
        // Trigger the edit via existing handler
        // This will call the edit endpoint and handle completion
        handleGenerateSection(section, true, instructions);
        
      } catch (error) {
        logger.error('Failed to process section edit:', error);
        setError(`Failed to edit section: ${toErrorMessage(error)}`);
      }
    };

    const handleSectionEditFailed = (event: Event) => {
      const customEvent = event as CustomEvent<{
        sectionId: string;
        projectId: number;
        error: string;
        source: string;
      }>;
      
      const { sectionId, error: errorMsg, source } = customEvent.detail || {};
      
      logger.error(`Section edit failed from ${source}:`, errorMsg);
      
      // Remove from generating set
      if (sectionId) {
        setSectionsGenerating(prev => {
          const newSet = new Set(prev);
          newSet.delete(sectionId as ArchitectureSection);
          return newSet;
        });
      }
      
      setError(`Section edit failed: ${errorMsg}`);
    };

    globalThis.addEventListener('sectionEditStarted', handleSectionEditStarted as EventListener);
    globalThis.addEventListener('sectionEditFailed', handleSectionEditFailed as EventListener);

    return () => {
      globalThis.removeEventListener('sectionEditStarted', handleSectionEditStarted as EventListener);
      globalThis.removeEventListener('sectionEditFailed', handleSectionEditFailed as EventListener);
    };
  }, [projectId, handleGenerateSection, setSectionsGenerating, setError, setSelectedSection]);

  // Listen for ADR update events
  useEffect(() => {
    if (!projectId) {return;}
    
    
    const reloadAdrTimestamps = async () => {
      try {
        const adrs = await adrService.getADRDefinitions(Number.parseInt(projectId, 10));
        const latestAdrTimestamp = calculateMostRecentAdrTimestamp(adrs);
        
        
        // Persist to project state for future initial loads
        if (latestAdrTimestamp) {
          try {
            const existingState = await projectStateService.getProjectState(Number.parseInt(projectId, 10));
            await projectStateService.saveProjectState(Number.parseInt(projectId, 10), {
              adrs: {
                ...(existingState?.adrs || {
                  decisions: [],
                  templates: [],
                  lastModified: new Date().toISOString(),
                }),
                lastActionTimestamp: latestAdrTimestamp,
              },
            });
          } catch (persistErr) {
            logger.warn('[ADR Event] Failed to persist ADR timestamp:', persistErr);
          }
        }
        
        setProjectContext(prev => ({
          ...prev,
          adrUpdatedAt: latestAdrTimestamp || undefined,
        }));
      } catch (err) {
        logger.error('Failed to reload ADR timestamps:', err);
      }
    };
    
    // Set up event listener
    const unsubscribe = onAdrUpdated((eventProjectId, adrId, actionTimestamp) => {
      // Verify it's for this project
      if (eventProjectId === Number.parseInt(projectId, 10)) {
        if (actionTimestamp) {
          
          // Persist to project state for future initial loads
          (async () => {
            try {
              const existingState = await projectStateService.getProjectState(Number.parseInt(projectId, 10));
              await projectStateService.saveProjectState(Number.parseInt(projectId, 10), {
                adrs: {
                  ...(existingState?.adrs || {
                    decisions: [],
                    templates: [],
                    lastModified: new Date().toISOString(),
                  }),
                  lastActionTimestamp: actionTimestamp,
                },
              });
            } catch (err: unknown) {
              logger.warn('[ADR Event] Failed to persist action timestamp:', err);
            }
          })();
          
          setProjectContext(prev => ({
            ...prev,
            adrUpdatedAt: actionTimestamp,
          }));
        } else {
          reloadAdrTimestamps();
        }
      }
    });
    
    return () => {
      unsubscribe();
    };
  }, [projectId, calculateMostRecentAdrTimestamp]);

  return (
    <div className="min-h-screen bg-background-base">
      {/* Stage Navigation */}
      {project && (
        <StageNavigationBar
          projectId={project.id}
          projectStatus={(() => {
            const statusMap: Record<string, ProjectStatus> = {
              'context_upload': ProjectStatus.CONTEXT_UPLOAD,
              'context_review': ProjectStatus.CONTEXT_REVIEW,
              'adr': ProjectStatus.ADR,
              'generation': ProjectStatus.GENERATION,
              'bizzdesign_integration': ProjectStatus.BIZZDESIGN_INTEGRATION,
              'completed': ProjectStatus.COMPLETED,
            };
            return statusMap[project.status] ?? ProjectStatus.CONTEXT_UPLOAD;
          })()}
          currentStage={5}
          isBrownfield={project?.projectType === ProjectType.BROWN_FIELD}
          onStageClick={(stageId: number, route: string) => navigate(route)}
        />
      )}

      {/* Error Message */}
      {error && (
        <div className={`${STAGE_PAGE_WIDTH_CLASS} py-4`}>
          <div className="alert alert-error mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className={`${STAGE_PAGE_WIDTH_CLASS} py-8`}>
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center gap-3">
              <div className="loading loading-spinner loading-primary"></div>
              <span className="text-text-onSurface-subtle">Loading project details...</span>
            </div>
          </div>
        </div>
      ) : (
        /* Main Generation Dashboard */
        <GenerationDashboard
          projectContext={projectContext}
          generationState={generationState}
          logs={logs}
          sectionsGenerating={sectionsGenerating}
          pendingGroundedness={pendingGroundedness}
          selectedSection={selectedSection}
          onConfigChange={handleConfigChange}
          onStartGeneration={handleStartGeneration}
          onPauseGeneration={handlePauseGeneration}
          onResumeGeneration={handleResumeGeneration}
          onCancelGeneration={handleCancelGeneration}
          onGenerateSection={handleGenerateSection}
          onUpdateSection={handleUpdateSection}
          onSectionSelect={handleSectionSelect}
        />
      )}

      {/* Bottom Navigation */}
      {!isLoading && (
        <div className={`${STAGE_PAGE_WIDTH_CLASS} pb-8`}>
          <BottomNavigationBar
            backAction={{
              label: BACK_BUTTON_LABEL,
              onClick: () => navigate(`/project/${projectId}/adrs`),
            }}
            primaryActions={[
              {
                label: CONTINUE_BUTTON_LABEL,
                onClick: async () => {
                  if (projectId) {
                    await projectStateService.advanceProjectStatus(
                      Number(projectId),
                      ProjectStatus.BIZZDESIGN_INTEGRATION,
                    );
                  }
                  navigate(`/project/${projectId}/c4`);
                },
                variant: 'primary',
              },
            ]}
          />
        </div>
      )}

      {/* Fresh regeneration confirmation modal (when regenerating without instructions) */}
      {showFreshRegenerationConfirm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50" style={{ zIndex: 9999 }}>
          <div className="bg-white rounded-lg shadow-xl max-w-xl w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-border-subtle">
              <h3 className="text-lg font-semibold text-text-onSurface-strong">Confirm regeneration</h3>
              <button
                onClick={() => {
                  // Immediately hide the confirmation modal so the UI closes
                  setShowFreshRegenerationConfirm(false);
                  if (freshRegenerationResolver.current) {freshRegenerationResolver.current(false);}
                  // Notify any open regenerate-instructions modal to close
                  globalThis.dispatchEvent(new CustomEvent('closeRegenerateModal'));
                  globalThis.dispatchEvent(new CustomEvent('globalCloseAllModals'));
                }}
                className="text-text-onSurface-subtle hover:text-text-onSurface-strong transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <p className="text-sm text-text-onSurface-subtle mb-4">
                No custom edit instructions were provided. Regenerating will replace the
                existing section content with a fresh generation that does not try to
                preserve the current content.
              </p>
              <p className="text-sm text-text-onSurface-subtle">Do you want to continue with a fresh regeneration?</p>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-border-subtle bg-background-base">
              <button
                onClick={() => {
                  // Immediately hide the confirmation modal so the UI closes
                  setShowFreshRegenerationConfirm(false);
                  if (freshRegenerationResolver.current) {freshRegenerationResolver.current(false);}
                  // Notify any open regenerate-instructions modal to close
                  globalThis.dispatchEvent(new CustomEvent('closeRegenerateModal'));
                  globalThis.dispatchEvent(new CustomEvent('globalCloseAllModals'));
                }}
                className="px-4 py-2 text-sm font-medium text-text-onSurface-strong bg-white border border-border-subtle rounded-lg hover:bg-background-base transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Immediately hide the confirmation modal so the UI closes
                  setShowFreshRegenerationConfirm(false);
                  if (freshRegenerationResolver.current) {freshRegenerationResolver.current(true);}
                  // Notify any open regenerate-instructions modal to close
                  globalThis.dispatchEvent(new CustomEvent('closeRegenerateModal'));
                  globalThis.dispatchEvent(new CustomEvent('globalCloseAllModals'));
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-accent-primary-strong rounded-lg hover:bg-accent-primary-stronger transition-colors inline-flex items-center gap-2"
              >
                Continue and regenerate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
